import { DEFAULT_CURRENCY, followupComparisonVariant, isFollowupPortfolio } from "@repo/shared";
import type { GoalPortfolioResponse } from "@repo/shared/types";
import { useCallback } from "react";
import { isComponentAllowedOnPageType } from "@/components/flow/flow-component-types";
import type { CompletionMarkers } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { FlowLanguageScope } from "@/lib/i18n/flow-language-scope";
import type { SnapshotRefreshHints } from "@/lib/types/goal";
import { CustomPortfolioBuilder } from "@/pages/flow-instances/goals/custom-portfolio-builder";
import { FollowupSourceNotice } from "@/pages/flow-instances/goals/followup-source-notice";
import { PortfolioInputWidget } from "@/pages/flow-instances/goals/portfolio-input-widget";
import {
  renderEmbed,
  renderFile,
  renderIframe,
  renderImage,
  renderModal,
  renderSpacer,
  renderTextBlock,
  renderYoutubeVideo,
} from "../flow-component-renderers";
import { MisplacedComponentNotice } from "../misplaced-component-notice";
import { AssetAllocation } from "../self-contained/asset-allocation";
import { CostBenefit } from "../self-contained/cost-benefit";
import { CostChart } from "../self-contained/cost-chart";
import { CostTable } from "../self-contained/cost-table";
import { ExpectedValue } from "../self-contained/expected-value";
import { HistoricalReturn } from "../self-contained/historical-return";
import { MandateWidget } from "../self-contained/mandate-widget";
import { PortfolioCosts } from "../self-contained/portfolio-costs";
import { PortfolioDisplay } from "../self-contained/portfolio-display";
import { PortfolioForecast } from "../self-contained/portfolio-forecast";
import { PortfolioProductCosts } from "../self-contained/portfolio-product-costs";
import { PortfolioXray } from "../self-contained/portfolio-xray";
import { SustainabilityStatus } from "../self-contained/sustainability-status";
import type { PortfolioInfo } from "../self-contained/types";
import type { PortfolioCost } from "./flow-wizard-types";
import { WizardFieldRenderer } from "./wizard-field-renderer";
import { WizardPageRenderer } from "./wizard-page-renderer";

type PortfolioPageRendererProps = {
  /** One portfolio page definition (layouts + standalone components). */
  page: any;
  /** The single resolved portfolio this page is rendered for. */
  portfolio: GoalPortfolioResponse;
  /**
   * The advisor flow-instance id — needed by portfolio components whose API is
   * keyed by instanceId (e.g. sustainability_status, mandate).
   */
  instanceId?: string | null;
  goalId: string;
  goals: any[];
  isCompleted: boolean;
  flowLanguage: string;
  conditionData: any;
  chartColors?: string[] | null;
  /** QL-403 — the collection's portfolio cost brackets, shown by `portfolio_costs`. */
  portfolioCosts?: PortfolioCost[];
  /** Flow currency the portfolio cost brackets are shown in. */
  currency?: string;
  showValidationErrors?: boolean;
  markers: CompletionMarkers;
  /**
   * QP-185 — the collection's opt-in dynamic setting; gates the "dynamic"
   * option in the portfolio type selector (disabled + annotation when off).
   */
  dynamicPortfoliosEnabled: boolean;
  /**
   * QL-72 — the collection allows a model portfolio from another risk score;
   * shows the model-portfolio picker on model portfolios.
   */
  modelDeviationEnabled?: boolean;
  /**
   * Suitability submit-blocker feed (keyed by portfolioId). Called by the
   * custom-portfolio builder when `onlyAllowSuitableProducts` is on and the
   * portfolio contains an unsuitable product.
   */
  onSuitabilityBlockerChange?: (portfolioId: string, hasBlocker: boolean) => void;
  /** QL-314 — carries refreshHints from portfolio mutations up to the goal-level
   *  snapshot invalidator so the volatility chart + asset class forecast refetch. */
  onSaveComplete?: (refreshHints?: SnapshotRefreshHints) => void | Promise<void>;
};

/**
 * Renders one portfolio page for a single, already-resolved portfolio (QP-150).
 *
 * This is the leaf extracted from the former PortfolioTabsContainer: it owns only the
 * per-portfolio component switch + the page layout render — no portfolio-instance tab strip and
 * no `multiPortfolio` branching. The nav layer (GoalContextView) decides which portfolio + page
 * to render and calls this once. Advisor-only: the public flow renders portfolio pages
 * through its own mirror, `PublicPortfolioPageRenderer`.
 */
export function PortfolioPageRenderer({
  page,
  portfolio,
  instanceId = null,
  goalId,
  goals,
  isCompleted,
  flowLanguage,
  conditionData,
  chartColors,
  portfolioCosts = [],
  currency = DEFAULT_CURRENCY,
  showValidationErrors,
  markers,
  dynamicPortfoliosEnabled,
  modelDeviationEnabled = false,
  onSuitabilityBlockerChange,
  onSaveComplete,
}: PortfolioPageRendererProps) {
  const goal = goals.find((g) => g.id === goalId) ?? null;
  const portfolioId: string | undefined = portfolio?.id;

  // Stable identity — the builder's effects depend on this callback, so a
  // fresh inline closure per render would ping-pong its mount/cleanup effects
  // into an infinite set-true/set-false loop. Re-keys by portfolioId (several
  // portfolios can exist per goal).
  const handleSuitabilityBlockerChange = useCallback(
    (_goalId: string, hasBlocker: boolean) => {
      if (portfolioId) onSuitabilityBlockerChange?.(portfolioId, hasBlocker);
    },
    [onSuitabilityBlockerChange, portfolioId],
  );

  if (!portfolio || !portfolioId) return null;

  // Both follow-up lineages light up the existing-vs-modified comparison
  // views; the shared predicates are the single definition of the rule.
  const isFollowup = isFollowupPortfolio(portfolio as any);
  const comparisonVariant = followupComparisonVariant(portfolio as any);

  const renderPortfolioComponent = (component: any) => {
    const componentHasError = !!(
      showValidationErrors &&
      markers.missingPortfolioComponentKeys.has(`${portfolioId}:${component.id}`)
    );

    if (!isComponentAllowedOnPageType(component.componentType, "portfolio")) {
      return (
        <MisplacedComponentNotice
          componentType={component.componentType}
          pageType="portfolio"
          language={flowLanguage}
        />
      );
    }

    const portfolioInfo: PortfolioInfo = {
      isCustomPortfolio: portfolio.portfolioType === "custom",
      hasRiskAssessment: !!goal?.hasRiskAssessment,
      hasInvestmentAmount:
        Number(portfolio.oneTimeAmount ?? 0) > 0 || Number(portfolio.recurringAmount ?? 0) > 0,
      riskScore: goal?.riskScore ?? null,
    };

    const cfg = component.config ? JSON.parse(component.config) : {};

    switch (component.componentType) {
      case "portfolio_input": {
        const allowedPortfolioTypes = cfg.allowedPortfolioTypes || ["model", "custom", "dynamic"];
        return (
          <PortfolioInputWidget
            goalId={goalId}
            portfolioId={portfolioId}
            initialData={{
              name: portfolio.name ?? "",
              scheduleType: portfolio.scheduleType,
              oneTimeAmount: portfolio.oneTimeAmount ?? undefined,
              oneTimeDate: portfolio.oneTimeDate,
              recurringAmount: portfolio.recurringAmount ?? undefined,
              recurringFrequency: portfolio.recurringFrequency,
              recurringStartDate: portfolio.recurringStartDate,
              recurringEndDate: portfolio.recurringEndDate,
              portfolioType: (portfolio.portfolioType ?? "model") as "model" | "custom" | "dynamic",
            }}
            disabled={isCompleted}
            followupSource={portfolio.followupSource ?? null}
            showPortfolioName={cfg.showPortfolioName !== false}
            showPortfolioType={cfg.showPortfolioType !== false}
            allowedPortfolioTypes={allowedPortfolioTypes}
            // QL-664 — a mandate template governs the goal: custom only.
            templateRestricted={goal?.templateRestricted ?? false}
            dynamicPortfoliosEnabled={dynamicPortfoliosEnabled}
            modelDeviationEnabled={modelDeviationEnabled}
            pickedPortfolioId={portfolio.pickedPortfolioId ?? null}
            showDateInputs={cfg.showDateInputs}
            style={cfg.style}
            title={cfg.title}
            description={cfg.description}
            hasValidationError={componentHasError}
            missingReason={markers.missingComponentReasons.get(`${portfolioId}:${component.id}`)}
            goalRiskScore={goal?.riskScore ?? null}
            minBuyViolations={portfolio.minBuyViolations ?? []}
            accountMinimumViolations={portfolio.accountMinimumViolations ?? []}
            generatedPositions={portfolio.generatedPositions ?? []}
            dynamicGeneration={portfolio.dynamicGeneration ?? null}
            instanceId={instanceId}
          />
        );
      }
      case "custom_portfolio":
        return (
          <CustomPortfolioBuilder
            portfolioId={portfolioId}
            goalId={goalId}
            style={cfg.style}
            title={cfg.title}
            description={cfg.description}
            showIsin={cfg.showIsin}
            showProductType={cfg.showProductType}
            onlyAllowSuitableProducts={cfg.onlyAllowSuitableProducts}
            goals={goals}
            isCompleted={isCompleted}
            hasValidationError={componentHasError}
            missingReason={markers.missingComponentReasons.get(`${portfolioId}:${component.id}`)}
            onSuitabilityBlockerChange={
              onSuitabilityBlockerChange ? handleSuitabilityBlockerChange : undefined
            }
            onSaveComplete={onSaveComplete}
          />
        );
      case "portfolio_display":
        return (
          <PortfolioDisplay
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            chartColors={chartColors}
            portfolioInfo={portfolioInfo}
            hasValidationError={componentHasError}
          />
        );
      case "portfolio_xray":
        return (
          <PortfolioXray
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            chartColors={chartColors}
            portfolioInfo={portfolioInfo}
            hasValidationError={componentHasError}
            showFollowupComparison={isFollowup && cfg.enableFollowupComparison === true}
            comparisonVariant={comparisonVariant}
          />
        );
      case "asset_allocation":
        return (
          <AssetAllocation
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            chartColors={chartColors}
            portfolioInfo={portfolioInfo}
            hasValidationError={componentHasError}
            showFollowupComparison={isFollowup && cfg.enableFollowupComparison === true}
            comparisonVariant={comparisonVariant}
          />
        );
      case "historical_return":
        return (
          <HistoricalReturn
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            portfolioInfo={portfolioInfo}
            hasValidationError={componentHasError}
          />
        );
      case "cost_table":
        return (
          <CostTable
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            portfolioInfo={portfolioInfo}
            hasValidationError={componentHasError}
          />
        );
      case "cost_chart":
        return (
          <CostChart
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            portfolioInfo={portfolioInfo}
            chartColors={chartColors}
            hasValidationError={componentHasError}
          />
        );
      // QL-375 — this portfolio's own weights against the goal's mandate
      // (informational; the goal page carries the gate). Needs the instance id.
      case "mandate":
        if (!instanceId) return null;
        return (
          <MandateWidget
            instanceId={instanceId}
            goalId={goalId}
            scope="portfolio"
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            hasValidationError={componentHasError}
            disabled={isCompleted}
          />
        );
      case "cost_benefit":
        return (
          <CostBenefit
            portfolioId={portfolioId}
            config={cfg}
            portfolioInfo={portfolioInfo}
            isOneTimePortfolio={portfolio.scheduleType === "one_time"}
            comparisonVariant={comparisonVariant}
            hasValidationError={componentHasError}
          />
        );
      case "portfolio_forecast":
        return (
          <PortfolioForecast
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            hasValidationError={componentHasError}
          />
        );
      case "expected_value":
        return (
          <ExpectedValue
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            portfolioInfo={portfolioInfo}
            hasValidationError={componentHasError}
          />
        );
      case "portfolio_costs":
        return (
          <PortfolioCosts
            portfolioCosts={portfolioCosts}
            config={cfg}
            currency={currency}
            instanceId={instanceId}
            locked={isCompleted}
          />
        );
      case "product_costs":
        return (
          <PortfolioProductCosts
            portfolioId={portfolioId}
            config={cfg}
            language={flowLanguage}
            instanceId={instanceId}
            locked={isCompleted}
          />
        );
      case "sustainability_status":
        // QP-179 — per-portfolio sustainability matching table + (advisor-only,
        // allowOverride-gated) deviation decision. Surface-agnostic.
        return (
          <SustainabilityStatus
            portfolioId={portfolioId}
            instanceId={instanceId}
            config={cfg}
            language={flowLanguage}
            disabled={isCompleted}
            hasValidationError={componentHasError}
          />
        );
      case "field": {
        if (!component.flowField || !instanceId) return null;
        const fieldKey =
          component.flowField.scope === "portfolio"
            ? `${portfolioId}:${component.flowField.id}`
            : component.flowField.scope === "goal"
              ? `${goalId}:${component.flowField.id}`
              : component.flowField.id;
        const fieldHasError = !!(showValidationErrors && markers.missingFieldKeys?.has(fieldKey));
        return (
          <WizardFieldRenderer
            field={component.flowField}
            instanceId={instanceId}
            isCompleted={isCompleted}
            goalId={goalId}
            portfolioId={portfolioId}
            hasValidationError={fieldHasError}
          />
        );
      }
      case "text_block":
        return renderTextBlock(component, flowLanguage);
      case "spacer":
        return renderSpacer(component);
      case "youtube_video":
        return renderYoutubeVideo(component);
      case "iframe":
        return renderIframe(component);
      case "image":
        return renderImage(component);
      case "embed":
        return renderEmbed(component);
      case "modal":
        return renderModal(component, flowLanguage);
      case "file":
        return renderFile(component, flowLanguage);
      default:
        return null;
    }
  };

  // The follow-up notice sits inside the scope too: the public flow shows it in
  // the flow's language, so the advisor preview matches.
  return (
    <FlowLanguageScope language={flowLanguage}>
      <div className="space-y-6">
        {portfolio.followupSource && <FollowupSourceNotice source={portfolio.followupSource} />}
        <WizardPageRenderer
          layouts={page.layouts || []}
          components={page.components || []}
          conditionData={{ ...conditionData, goalId, portfolioId }}
          renderComponent={renderPortfolioComponent}
          showValidationErrors={showValidationErrors}
          missingTabKeys={markers.missingTabKeys}
        />
      </div>
    </FlowLanguageScope>
  );
}
